import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Manages the locked cells warning and info banners
// Shows warning when there are locked columns but no password is set
// Shows info when there is a password but no locked columns
//
// Usage:
// 1. In report sheet forms (columns in DOM):
//    - Controller queries DOM for input[data-column-data="locked"] fields
//    - Listens for 'column-locked-state-changed' events
//
// 2. In report details form (no columns in DOM):
//    - Set hasLockedColumns value from server
//    - Add passwordInput target to watch for password field changes
export default class extends Controller {
  static targets = ["warning", "info", "passwordInput"]
  static values = {
    hasPassword: Boolean,
    hasLockedColumns: Boolean  // Server-provided, used when columns aren't in DOM
  }

  connect() {
    logger.log("[Locked Cells Warning] Controller connected, hasPassword:", this.hasPasswordValue, "hasLockedColumns value:", this.hasLockedColumnsValue)
    this.checkAndUpdateWarning()

    // Listen for column locked state changes (for report sheet forms)
    this.element.addEventListener('column-locked-state-changed', this.handleLockedStateChanged.bind(this))

    // Watch password input field changes (for report details form)
    if (this.hasPasswordInputTarget) {
      this.boundHandlePasswordInput = this.handlePasswordInput.bind(this)
      this.passwordInputTarget.addEventListener('input', this.boundHandlePasswordInput)
    }
  }

  disconnect() {
    this.element.removeEventListener('column-locked-state-changed', this.handleLockedStateChanged.bind(this))
    if (this.hasPasswordInputTarget && this.boundHandlePasswordInput) {
      this.passwordInputTarget.removeEventListener('input', this.boundHandlePasswordInput)
    }
  }

  // Event handler for when a column's locked state changes
  handleLockedStateChanged(event) {
    logger.log("[Locked Cells Warning] Column locked state changed:", event.detail)
    this.checkAndUpdateWarning()
  }

  // Event handler for when password input value changes
  handlePasswordInput(event) {
    this.hasPasswordValue = event.target.value.trim().length > 0
    logger.log("[Locked Cells Warning] Password input changed, hasPassword:", this.hasPasswordValue)
    this.checkAndUpdateWarning()
  }

  // Check if there are any locked columns and update warning/info visibility
  checkAndUpdateWarning() {
    // Use server-provided value if explicitly set, otherwise query DOM
    // Check for the data attribute to know if the value was explicitly provided
    const hasLockedColumnsAttr = this.element.hasAttribute('data-locked-cells-warning-has-locked-columns-value')
    const hasLocked = hasLockedColumnsAttr
      ? this.hasLockedColumnsValue
      : this.getLockedColumns().length > 0

    const shouldShowWarning = hasLocked && !this.hasPasswordValue
    const shouldShowInfo = this.hasPasswordValue && !hasLocked

    logger.log("[Locked Cells Warning] Has locked:", hasLocked, "(from attr:", hasLockedColumnsAttr, "), Has password:", this.hasPasswordValue, "Show warning:", shouldShowWarning, "Show info:", shouldShowInfo)

    if (shouldShowWarning) {
      this.showWarning()
    } else {
      this.hideWarning()
    }

    if (shouldShowInfo) {
      this.showInfo()
    } else {
      this.hideInfo()
    }
  }

  // Get all locked columns from the form
  getLockedColumns() {
    // Find all hidden fields with data-column-data="locked" and value="true"
    // These are the actual form fields that get submitted
    const lockedFields = this.element.querySelectorAll('input[data-column-data="locked"]')
    const lockedColumns = Array.from(lockedFields).filter(field => field.value === 'true')
    return lockedColumns
  }

  showWarning() {
    if (this.hasWarningTarget) {
      this.warningTarget.classList.remove('d-none')
      logger.log("[Locked Cells Warning] Warning shown")
    }
  }

  hideWarning() {
    if (this.hasWarningTarget) {
      this.warningTarget.classList.add('d-none')
      logger.log("[Locked Cells Warning] Warning hidden")
    }
  }

  showInfo() {
    if (this.hasInfoTarget) {
      this.infoTarget.classList.remove('d-none')
      logger.log("[Locked Cells Warning] Info shown")
    }
  }

  hideInfo() {
    if (this.hasInfoTarget) {
      this.infoTarget.classList.add('d-none')
      logger.log("[Locked Cells Warning] Info hidden")
    }
  }
}
